/**
 * TestListColumn.tsx
 * Admin → Test Mapping (master-detail redesign, v1.0.34) — the left column:
 * searchable, filterable list of lab tests. One row per test with its WO-line
 * impact, status chip, and (when mapped) the catalog item it maps to.
 * Clicking a row selects it into the detail pane.
 *
 * Pure list logic (filter/sort/status) lives in mappingModel.ts.
 */

import {
  Box, Chip, InputAdornment, List, ListItemButton, TextField, Typography,
  alpha, useTheme,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import type { Theme } from '@mui/material/styles';
import type { MappingStudioTest } from '../../../services/testMappingService';
import {
  deriveTestStatus,
  type TestListFilter,
  type TestMappingStatus,
} from './mappingModel';

// ── Status chip styling (theme.admin mapping tokens) ─────────────────────────

interface StatusChipMeta {
  label: string;
  color: string;
  filled: boolean;
}

const statusChipMeta = (status: TestMappingStatus, theme: Theme): StatusChipMeta => {
  switch (status) {
    case 'unmapped':
      return { label: 'Unmapped', color: theme.admin.mappingUnconfirmed, filled: true };
    case 'unconfirmed':
      return { label: 'Unconfirmed', color: theme.admin.mappingUnconfirmed, filled: false };
    case 'confirmed':
      return { label: 'Confirmed', color: theme.admin.mappingConfirmed, filled: false };
    case 'nameMatch':
      return { label: 'Name match', color: theme.admin.mappingNameMatch, filled: false };
  }
};

export const TestStatusChip = ({ status }: { status: TestMappingStatus }) => {
  const theme = useTheme();
  const meta = statusChipMeta(status, theme);
  return (
    <Chip
      size="small"
      label={meta.label}
      sx={{
        height: 20,
        fontSize: '0.65rem',
        fontWeight: 700,
        backgroundColor: alpha(meta.color, meta.filled ? 0.18 : 0.07),
        color: meta.color,
        border: meta.filled ? 'none' : `1px solid ${alpha(meta.color, 0.5)}`,
      }}
    />
  );
};

// ── Filter chips ──────────────────────────────────────────────────────────────

const FILTERS: { key: TestListFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unmapped', label: 'Unmapped' },
  { key: 'unconfirmed', label: 'Unconfirmed' },
  { key: 'confirmed', label: 'Confirmed' },
];

// ── Column ────────────────────────────────────────────────────────────────────

interface TestListColumnProps {
  /** Already filtered + sorted (mappingModel.filterAndSortTests). */
  tests: MappingStudioTest[];
  counts: Record<TestListFilter, number>;
  search: string;
  onSearchChange: (value: string) => void;
  filter: TestListFilter;
  onFilterChange: (value: TestListFilter) => void;
  selectedTestKey: string | null;
  onSelect: (testKey: string) => void;
  /** Resolves a catalog item key → display name (for the mapped-to line). */
  catalogNameByKey: (key: string) => string | null;
}

export const TestListColumn = ({
  tests,
  counts,
  search,
  onSearchChange,
  filter,
  onFilterChange,
  selectedTestKey,
  onSelect,
  catalogNameByKey,
}: TestListColumnProps) => {
  const theme = useTheme();

  const mappedToName = (t: MappingStudioTest): string | null => {
    const key = t.mapping?.TestCatalogItemKey ?? t.exactNameMatchKey;
    return key ? catalogNameByKey(key) : null;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <TextField
        size="small"
        placeholder="Search lab tests…"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        sx={{ mb: 1 }}
      />

      <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
        {FILTERS.map(({ key, label }) => {
          const active = filter === key;
          return (
            <Chip
              key={key}
              size="small"
              clickable
              label={`${label} (${counts[key]})`}
              onClick={() => onFilterChange(key)}
              sx={{
                fontWeight: active ? 700 : 500,
                fontSize: '0.7rem',
                backgroundColor: active
                  ? alpha(theme.palette.primary.main, 0.14)
                  : 'transparent',
                color: active ? theme.palette.primary.main : theme.palette.text.secondary,
                border: `1px solid ${active ? theme.palette.primary.main : theme.border.color}`,
              }}
            />
          );
        })}
      </Box>

      <List dense disablePadding sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {tests.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
            No lab tests match this search / filter.
          </Typography>
        )}
        {tests.map((t) => {
          const status = deriveTestStatus(t);
          const selected = t.TestKey === selectedTestKey;
          const target = mappedToName(t);
          return (
            <ListItemButton
              key={t.TestKey}
              selected={selected}
              onClick={() => onSelect(t.TestKey)}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                border: `1px solid ${selected ? theme.palette.primary.main : theme.border.color}`,
                alignItems: 'flex-start',
                flexDirection: 'column',
                gap: 0.25,
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontWeight: status === 'unmapped' ? 700 : 600,
                    fontSize: '0.8rem',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {t.TestName}
                </Typography>
                <Chip
                  size="small"
                  label={`${t.woLineCount} line${t.woLineCount === 1 ? '' : 's'}`}
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    backgroundColor: alpha(theme.schedule.machineTime, 0.1),
                    color: theme.schedule.machineTime,
                  }}
                />
                <TestStatusChip status={status} />
              </Box>
              {target && (
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ color: theme.palette.text.secondary, width: '100%', fontSize: '0.68rem' }}
                >
                  → {target}
                </Typography>
              )}
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};
